import { Writer } from './Writer.js';
import {
  darkGrey,
  green,
  lightRed,
  red,
  yellow,
  white,
  bold,
  blue,
  cyan,
  magenta,
  setAnsiDisabled,
  gradient,
  type Formatter,
} from './ansiHelpers.js';
import { TaskReporterTask, type TaskReporterTaskResult } from './TaskReporterTask.js';
import type { LogMessageType, TaskReporterCompleteTaskStatus } from './types/TaskReporterTaskStatus.js';
import { formatTime } from './formatTime.js';
import { getTimestamp } from './getTimestamp.js';
import type { Sticky } from './Sticky.js';
import { SpinningSticky } from './SpinningSticky.js';
import { prefixCharacters } from './prefixCharacters.js';
import { formatMemoryString } from './formatMemoryString.js';
import { defaultLoggingConfig } from './loggingConfigs.js';
import { bulletedList } from './bulletedList.js';
import type { ConsoleLogMethod } from './types/ConsoleLogMethod.js';
import type { TaskReporterLogLevelOptions } from './types/TaskReporterLogLevelOptions.js';

/** Controls how many tasks should be displayed in the pending sticky list. */
const maxRunningTasksShown = 5;

/** Prefix (and color if enabled) for each task status and console log method */
const messagePrefixDefs: Record<LogMessageType, { prefix: string; color: Formatter }> = {
  running: { prefix: 'started', color: yellow },
  complete: { prefix: 'completed', color: green },
  fail: { prefix: 'failed', color: red },
  abort: { prefix: 'aborted', color: yellow },
  skip: { prefix: 'skipped', color: darkGrey },
  pending: { prefix: 'pending', color: darkGrey },
  warn: { prefix: 'warn', color: yellow },
  log: { prefix: 'log', color: darkGrey },
  info: { prefix: 'info', color: blue },
  error: { prefix: 'error', color: red },
  debug: { prefix: 'debug', color: magenta },
};

/** Mapping from console log method to controlling option name */
const consoleOverrides: Record<ConsoleLogMethod, keyof TaskReporterLogLevelOptions> = {
  log: 'showConsoleLog',
  warn: 'showConsoleWarn',
  info: 'showConsoleInfo',
  error: 'showConsoleError',
  debug: 'showConsoleDebug',
};

/**
 * Options for the TaskReporter instance.
 */
export interface TaskReporterOptions extends TaskReporterLogLevelOptions {
  /**
   * Product name to display.
   */
  productName?: string;

  /**
   * Version of the product to display.
   */
  version?: string;

  /**
   * Description of the product to display next to the product name.
   * If it uses ANSI codes, use the function version to ensure plain text mode is respected.
   */
  description?: string | (() => string);

  /**
   * Help message to display.
   */
  helpMessage?: string;

  /**
   * Plain text mode. This will disable sticky rendering which requires ansi support.
   */
  plainTextMode?: boolean;

  /**
   * Log all console output to a file
   */
  logFile?: boolean;

  /**
   * This will be used as the working directory for the log file.
   * Required if `logFile` is true.
   */
  cwd?: string;
}

interface TaskReporterCompleteParams {
  /**
   * If set, this will be printed instead of the default task summary.
   * Ignored if `TaskReporterOptions.showSummary` was false.
   */
  summary?: string;
  /**
   * If true, show the help message after the summary.
   * Ignored if `TaskReporterOptions.showSummary` was false.
   */
  showHelp?: boolean;
}

/**
 * A reporter for tasks. This is used to report the status of tasks to the console.
 */
export class TaskReporter {
  // This must be set to an empty object initially so that if initial options are provided,
  // setOptions will print the product info.
  private _options: TaskReporterOptions = {};
  private _writer = new Writer();
  private _pendingTasks = new Set<TaskReporterTask>();
  private _runningTasks = new Set<TaskReporterTask>();
  private _runningTaskStickies = new Map<TaskReporterTask, Sticky>();

  /**
   * This is used to store console outputs that are not part of a task and tasks that are forced to show
   */
  private _consoleOutputs = new Set<{ message: string; type: string; timestamp: Date } | TaskReporterTask>();

  /**
   * Delta in heap usage for each task (bytes). Only populated if `options.showHeapUsage` is set.
   * NOTE: These might not be meaningful since multiple tasks can execute in parallel (no way to
   * isolate which task actually used the memory), and we don't know when GC might happen.
   */
  private _taskHeapDeltas: Array<{ name: string; heapDelta: number }> = [];
  private _hasCompleted: boolean = false;
  private _ignoredLogMessages: string[] | undefined;
  private _maxHeapSize: number = 0;
  /** Number of completed tasks with each status */
  private _summary: Record<TaskReporterCompleteTaskStatus, number> = {
    complete: 0,
    fail: 0,
    abort: 0,
    skip: 0,
  };
  /** Number of completed tasks with any status */
  private _completedCount = 0;

  /** Prefixes for log messages, respecting the current `plainTextMode` setting */
  private _messagePrefixes: Record<LogMessageType, string>;

  constructor(options: TaskReporterOptions = defaultLoggingConfig) {
    // Catch console.log messages and print them using this.write.
    this._overrideConsoleLogging();

    // Initialize things based on options.
    this.setOptions(options);

    // This is actually done in `setOptions`, but TS doesn't know that
    this._messagePrefixes ??= getMessagePrefixes();
  }

  /**
   * Add a partial string to match on console logging. When found, we ignore the message.
   */
  public ignoreLogMessage(partialMatch: string): void {
    this._ignoredLogMessages ??= [];
    if (!this._ignoredLogMessages.includes(partialMatch)) {
      this._ignoredLogMessages.push(partialMatch);
    }
  }

  /**
   * Set options. If the product name changes, this will also print the product info.
   */
  public setOptions(options: TaskReporterOptions): void {
    if (this._hasCompleted) {
      throw new Error(`TaskReporter has already completed.`);
    }

    const previousOptions = this._options;

    this._options = { ...options };

    if (options.plainTextMode !== undefined) {
      setAnsiDisabled(options.plainTextMode);
    }

    // Configure log file if it's been specified
    if (options.logFile == true) {
      if (!options.cwd) {
        throw new Error(`TaskReporter logFile is true, but cwd is not provided.`);
      }
      this._writer.enableLogFile(options.cwd);
    }

    this._messagePrefixes = getMessagePrefixes();

    if (previousOptions.productName !== options.productName) {
      this._reportProductInfo();
    }
  }

  public getOptions(): Readonly<TaskReporterOptions> {
    return this._options;
  }

  /**
   * Add a task to the reporter. If `pending` is true, the task will be initially
   * in a waiting state. Otherwise it will assume to be in a started state.
   */
  public addTask(name: string, pending?: boolean): TaskReporterTask {
    if (this._hasCompleted) {
      throw new Error(`TaskReporter has already completed.`);
    }

    const task = new TaskReporterTask(name);
    let initialHeapSize = 0;

    task.on('start', () => {
      if (this._hasCompleted) return;

      // Getting memory usage may have some cost, so only do it if we'll be showing the info.
      if (this._options.showHeapUsage) {
        initialHeapSize = process.memoryUsage().heapUsed;
      }

      this._pendingTasks.delete(task);
      this._runningTasks.add(task);

      // Log the task start if requested. Also log if it's plain text mode and pending tasks are
      // supposed to be shown (the pending stickies don't show in plain text, so unless we log
      // the start, that info won't be available at all).
      if (this._options.showStarted || (this._options.showPending && this._options.plainTextMode)) {
        this._reportMessage('running', task.name);
      }

      this._updateStickies();
    });

    task.on('complete', (taskResult) => {
      if (this._hasCompleted) return;

      if (this._options.showHeapUsage) {
        // Only calculate and store heap usage if we'll show it.
        const heapDelta = Math.max(0, process.memoryUsage().heapUsed - initialHeapSize);
        this._taskHeapDeltas.push({ name: task.name, heapDelta });
      }

      this._runningTasks.delete(task);

      this._summary[taskResult.status]++;
      this._completedCount++;

      task.removeAllListeners();

      this._reportTaskComplete(task, taskResult);

      if (taskResult.forceShow) {
        this._consoleOutputs.add(task);
      }

      const sticky = this._runningTaskStickies.get(task);

      if (sticky) {
        sticky.remove();
        this._runningTaskStickies.delete(task);
      }

      this._updateStickies();
    });

    if (pending) {
      this._pendingTasks.add(task);
      this._updateStickies();
    } else {
      this._runningTasks.add(task);
      task.start();
    }

    return task;
  }

  public async complete(params: TaskReporterCompleteParams = {}): Promise<void> {
    if (this._hasCompleted) {
      throw new Error(`TaskReporter has already completed.`);
    }

    // Complete all the pending and running tasks. The complete handler for each task (defined
    // in addTask) will add these to the array of completed tasks.
    this._pendingTasks.forEach((task) => task.complete({ status: 'skip' }));
    this._runningTasks.forEach((task) => task.complete({ status: 'abort' }));
    this._pendingTasks.clear();
    this._runningTasks.clear();
    this._writer.stickies = [];

    this._reportSummary(params);

    this._hasCompleted = true;

    // Clean up resources
    await this._writer.dispose();
  }

  public hasErrors(): boolean {
    return this._summary.fail > 0;
  }

  /**
   * Determines the effective log method to use for a message.
   * This handles special cases like Node.js deprecation warnings that should be
   * treated as warnings even if they come through console.error.
   */
  private _getEffectiveLogMethod(originalMethod: ConsoleLogMethod, message: string): ConsoleLogMethod {
    // Check if this is a Node.js deprecation warning
    // Pattern: (node:PID) [DEPXXXX] DeprecationWarning: ...
    if (originalMethod === 'error' && this._isNodeDeprecationWarning(message)) {
      // Treat Node.js deprecation warnings as warnings instead of errors
      return 'warn';
    }

    return originalMethod;
  }

  /**
   * Detects if a message is a Node.js deprecation warning.
   * Node.js deprecation warnings typically follow the pattern:
   * (node:PID) [DEPXXXX] DeprecationWarning: ...
   */
  private _isNodeDeprecationWarning(message: string): boolean {
    // Match the Node.js deprecation warning pattern
    const nodeDeprecationPattern = /^\(node:\d+\)\s+\[DEP\d+\]\s+DeprecationWarning:/;
    return nodeDeprecationPattern.test(message);
  }

  private _overrideConsoleLogging(): void {
    const createOverride = (optionName: keyof TaskReporterOptions, method: ConsoleLogMethod) => {
      return (...args: unknown[]) => {
        if (this._options[optionName]) {
          const message = args.join(' ');
          if (!this._ignoredLogMessages?.some((substring) => message.includes(substring))) {
            // Check if this is a Node.js deprecation warning that should be treated as a warning
            // instead of an error, even if it came through console.error
            const finalMethod = this._getEffectiveLogMethod(method, message);

            // Get the option name for the final method to check if it should be shown
            const finalOptionName = consoleOverrides[finalMethod];

            // Only proceed if the final method should be shown
            if (this._options[finalOptionName]) {
              this._consoleOutputs.add({ message, type: finalMethod, timestamp: new Date() });
              this._reportMessage(finalMethod, message);
            }
          }
        }
      };
    };

    for (const [key, overrideName] of Object.entries(consoleOverrides)) {
      const method = key as ConsoleLogMethod;
      console[method] = createOverride(overrideName, method);
    }
  }

  private _reportProductInfo(): void {
    if (!this._options.showProductInfo) {
      return;
    }

    const { productName, version, description } = this._options;

    if (productName) {
      this._writer.write(
        [
          `\n${gradient(productName)}`,
          version && `(v${bold(white(version))})`,
          description && cyan(typeof description === 'string' ? description : description()),
        ]
          .filter(Boolean)
          .join(' '),
      );
    }

    this._writer.write('');
  }

  private _reportMessage(logType: LogMessageType, message: string, timestamp?: string): void {
    if (message.length) {
      this._writer.write(
        `${darkGrey(`[${timestamp ?? getTimestamp()}]`)} ${this._messagePrefixes[logType]} ${message}`,
      );
    }
  }

  private _reportTaskComplete(task: TaskReporterTask, taskResult: TaskReporterTaskResult): void {
    const taskStatus = taskResult.status;

    if (this._options.showHeapUsage) {
      const memory = process.memoryUsage();
      this._maxHeapSize = Math.max(this._maxHeapSize, memory.heapUsed);
    }

    // Show completed tasks if the option is set, or if the task was forced to show.
    if (this._options.showCompleted || taskResult.forceShow || (this._options.showErrors && taskStatus === 'fail')) {
      const resultDetails = taskResult.message
        ? `${taskResult.message[0] === '\n' ? '' : '- '}${taskResult.message}`
        : '';
      const resultDuration = task.duration && taskStatus !== 'abort' ? `(${formatTime(task.duration)})` : '';

      const endedAt = task.endedAt ? getTimestamp(task.endedAt) : undefined;

      this._reportMessage(taskStatus, `${task.name} ${resultDetails} ${darkGrey(resultDuration)}`, endedAt);

      if (taskResult.details && this._options.showTaskDetails) {
        this._writer.write(taskResult.details);
      }

      if (taskResult.extended && this._options.showTaskExtended) {
        this._writer.write(`Extended details:\n${taskResult.extended}`);
      }

      if (taskResult.errors?.length && this._options.showErrors) {
        this._writer.write(bulletedList(taskResult.errors, 1, lightRed(prefixCharacters.error)));
      }
    }
  }

  /**
   * Reset the reporter.
   * This will clear all tasks and messages, and reprint the product info, console outputs, and forced tasks.
   *
   * @param clearOutputs If true, clears the console outputs that were logged during the task run.
   */
  public reset(clearOutputs?: boolean): void {
    if (this._options.plainTextMode) {
      return;
    }

    this._writer.clear();
    this._reportProductInfo();

    if (clearOutputs) {
      this._consoleOutputs.clear();
    }

    for (const output of this._consoleOutputs) {
      if (output instanceof TaskReporterTask) {
        if (output.result) {
          this._reportTaskComplete(output, output.result);
        }
      } else {
        this._reportMessage(output.type as LogMessageType, output.message, getTimestamp(output.timestamp));
      }
    }
  }

  private _reportSummary(params: TaskReporterCompleteParams): void {
    if (!this._options.showSummary) {
      return;
    }

    if (params.summary !== undefined) {
      this._writer.write('');
      this._writer.write(params.summary);
    } else {
      // Report the final summary of pass/fail/skip/abort
      // (NOTE: As of writing, none of the commands use this any more; they all pass a custom complete reason.)
      const summary = this._summary;
      const summaryMessage = [
        summary.complete && `${green(summary.complete)} task(s) completed`,
        summary.fail && `${red(summary.fail)} failed`,
        summary.abort && `${yellow(summary.abort)} aborted`,
        summary.skip && `${darkGrey(summary.skip)} skipped`,
      ]
        .filter(Boolean)
        .join(', ');

      if (summaryMessage) {
        this._writer.write('');
        this._writer.write(`Summary:\n\n${summaryMessage}`);
      }
    }

    if (this._options.showHeapUsage) {
      this._maxHeapSize && this._writer.write(`\nMax heap size: ${green(formatMemoryString(this._maxHeapSize))}`);

      if (this._taskHeapDeltas.length) {
        const tasksByUsage = this._taskHeapDeltas
          .sort((a, b) => b.heapDelta - a.heapDelta)
          .slice(0, 10)
          .map((t) => `${t.name} (${formatMemoryString(t.heapDelta)})`);

        this._writer.write(
          `\nTop heap increments (note that these also include heap usage by other concurrent tasks):\n`,
        );
        this._writer.write(bulletedList(tasksByUsage));
      }
    }

    if (params.showHelp && this._options.helpMessage) {
      this._writer.write(this._options.helpMessage);
    }
  }

  private _updateStickies(): void {
    if (!this._options.showPending || this._options.plainTextMode) {
      return;
    }

    let heapUsage = '';
    if (this._options.showHeapUsage) {
      const memory = process.memoryUsage();
      heapUsage = `${green(formatMemoryString(memory.heapUsed))}`;
    }
    const summaryStickies = [
      '',
      [
        darkGrey('['),
        [
          this._runningTasks.size ? `Running: ${yellow(this._runningTasks.size)}` : 'Idle',
          this._pendingTasks.size && `Pending: ${yellow(this._pendingTasks.size)}`,
          this._completedCount && `Completed: ${green(this._completedCount)}`,
          heapUsage && `Heap: ${heapUsage}`,
        ]
          .filter(Boolean)
          .join(darkGrey(` | `)),
        darkGrey(`]`),
      ].join(' '),
    ];

    const runningTaskStickies = [
      ...Array.from(this._runningTasks)
        .slice(0, maxRunningTasksShown)
        .map((task) => {
          if (!this._runningTaskStickies.has(task)) {
            this._runningTaskStickies.set(task, new SpinningSticky(task.name));
          }
          return this._runningTaskStickies.get(task);
        }),
      this._runningTasks.size > maxRunningTasksShown &&
        `${yellow(`(+${this._runningTasks.size - maxRunningTasksShown} more...)`)}`,
    ].filter(Boolean) as (string | Sticky)[];

    this._writer.stickies = [...summaryStickies, ...runningTaskStickies];
  }
}

/**
 * Get uniform-length message prefix strings (character and text) which use colors if enabled.
 * It's unnecessary to re-generate these on every message, but they do need to be updated if
 * the `plainTextMode` changes. (The color functions respect the new mode automatically as long as
 * this is called after TaskReporter
 */
function getMessagePrefixes(): Record<LogMessageType, string> {
  const messagePrefixLength = Object.values(messagePrefixDefs).reduce(
    (max, { prefix }) => Math.max(max, prefix.length),
    0,
  );

  return Object.fromEntries(
    Object.entries(messagePrefixDefs).map(([_logType, { prefix, color }]): [LogMessageType, string] => {
      const logType = _logType as LogMessageType;
      const char = prefixCharacters[logType];
      // Only color the character if it's something other than the default "-"
      const maybeColoredChar = char === prefixCharacters.log ? char : color(char);
      const paddedPrefix = prefix.padEnd(messagePrefixLength, ' ');
      return [logType, `${maybeColoredChar} ${bold(color(paddedPrefix))}`];
    }),
  ) as Record<LogMessageType, string>;
}
